// Copyright 2017 Sorama B.V.

import {
    customElement,
    inject,
    bindable,
    bindingMode,
    BindingEngine,
    Disposable,
    ICollectionObserverSplice,
} from "aurelia-framework";

export class Category<T> {
    categoryFilter: (filterObject: T) => boolean;
    categorySorter: (categoryObjects: T[]) => void;
}

export class Categories<T> {
    [category: string]: Category<T>;
}

@customElement("category-table")
export class CategoryTable {
    //#region Injected Services
    private bindingEngine: BindingEngine;
    //#endregion

    //#region Bindable Properties
    @bindable items: unknown[];
    @bindable categories: Categories<unknown>;
    //#endregion

    //#region Fields
    public categoryItems: { [category: string]: unknown[] };

    private itemsWatcher: Disposable;
    //#endregion

    constructor(bindingEngine: BindingEngine) {
        this.bindingEngine = bindingEngine;

        this.items = [];
        this.categories = {};
        this.categoryItems = {};
    }

    public unbind(): void {
        if (this.itemsWatcher) {
            this.itemsWatcher.dispose();
            this.itemsWatcher = null;
        }
    }

    //#region Change Callbacks
    private itemsChanged(newValue: unknown[], oldValue: unknown[]) {
        if (this.itemsWatcher) this.itemsWatcher.dispose();

        if (!this.items) return;

        this.itemsWatcher = this.bindingEngine.collectionObserver(this.items).subscribe(this.itemsCollectionChanged);

        this.updateCategoryItems();
    }

    private itemsCollectionChanged = (changeRecords: ICollectionObserverSplice<unknown, unknown>[]) => {
        this.updateCategoryItems();
    };

    private categoriesChanged(newValue: Categories<unknown>, oldValue: Categories<unknown>) {
        this.updateCategoryItems();
    }
    //#endregion

    //#region Helper Methods
    private updateCategoryItems(): void {
        if (!this.items) return;

        const newCategoryItems: { [category: string]: unknown[] } = {};

        for (const category in this.categories) {
            newCategoryItems[category] = [];
        }

        // Add items to categories
        this.items.forEach((item: unknown) => {
            for (const category in this.categories) {
                if (this.categories[category].categoryFilter(item)) {
                    newCategoryItems[category].push(item);
                    break;
                }
            }
        });

        // Sort categories
        for (const category in this.categories) {
            this.categories[category].categorySorter(newCategoryItems[category]);
        }

        this.categoryItems = newCategoryItems;
    }
    //#endregion
}
